import {
  call,
  put,
  throttle,
  takeLatest,
  fork,
  select,
} from 'redux-saga/effects';
import { isEmpty } from 'lodash';
import { MFA_ENABLED } from 'domain/fields';
import { LIST_ERROR_TO_FETCH_LIST } from 'domain/user-management/consts';
import UserManagementApi from 'services/api/UserManagementApi';
// eslint-disable-next-line import/no-cycle
import {
  buildEditPayloadFromUser,
  prepareApiItem,
} from 'services/user-management/UserManagementService';
import handleError from 'services/error/handleScopeError';
import types from './types';
import actions from './actions';
import selectors from './selectors';

function* fetchList() {
  try {
    yield put(actions.requestStart(true));
    // Sort is handled on the client after fetch (see sortUserList).
    // Do not send sort here; paging + API sort would not mix with client sort.
    const {
      data: {
        data: { detail = [], metadata = {} },
      },
    } = yield call(UserManagementApi.fetchAll, {});
    yield put(
      actions.fetchedList({
        byIds: detail,
        metadata,
      })
    );
  } catch (error) {
    yield put(actions.requestFailed({ error: handleError(error.response) }));
  }
}

function* fetchListByCodeErrors() {
  const errors = yield select(selectors.getApiError);
  if (!isEmpty(errors)) {
    const listCode = errors.map((err) => err.code);
    const isFetchList = listCode.some((errorCode) =>
      LIST_ERROR_TO_FETCH_LIST.includes(errorCode)
    );
    if (isFetchList) {
      yield fork(fetchList);
    }
  }
}

function* create(action) {
  const { callback } = action;
  try {
    yield put(actions.requestStart());
    yield call(UserManagementApi.create, action.payload);
    yield fork(fetchList);
    callback();
  } catch (error) {
    yield put(
      actions.requestFailed({
        error: handleError(error.response, [400, 403]),
      })
    );
  }
}

function* update(action) {
  const { callback } = action;
  try {
    yield put(actions.requestStart());
    yield call(UserManagementApi.update, action.payload);
    yield fork(fetchList);
    callback();
  } catch (error) {
    yield put(
      actions.requestFailed({
        error: handleError(error.response, [400, 403]),
      })
    );
  }
}

function* bulkUpdateMfa(action) {
  const { users, mfaEnabled } = action.payload;
  const { callback } = action;

  yield put(actions.requestStart());

  const targets = users.filter(
    (user) => (user.mfa_enabled ?? false) !== mfaEnabled
  );

  // eslint-disable-next-line no-restricted-syntax
  const failedUsers = [];
  for (const user of targets) {
    try {
      const item = buildEditPayloadFromUser(user, {
        [MFA_ENABLED]: mfaEnabled,
      });
      const apiItem = prepareApiItem(item);
      yield call(UserManagementApi.update, {
        targetId: user.user_id,
        item,
        apiItem,
      });
    } catch (_err) {
      failedUsers.push(user);
    }
  }

  yield fork(fetchList);
  if (callback) callback({ failedUsers });
}

function* remove(action) {
  try {
    const id = action.payload;
    yield put(actions.requestStart(true));
    yield call(UserManagementApi.delete, id);
    yield put(actions.fetchList());
  } catch (error) {
    yield put(
      actions.requestFailed({
        error: handleError(error.response, [400, 403]),
      })
    );
  }
}

export default function* watchView() {
  yield takeLatest(types.FETCH_LIST, fetchList);
  yield throttle(1000, types.CREATE, create);
  yield throttle(1000, types.UPDATE, update);
  yield takeLatest(types.BULK_UPDATE_MFA, bulkUpdateMfa);
  yield throttle(1000, types.DELETE, remove);
  yield takeLatest(types.FETCH_LIST_BY_CODE, fetchListByCodeErrors);
}
